/**
 * Quem MAIS apaga etiqueta — e o que isso faz com a verdade do `daily_ops`
 * (issue #354).
 *
 * ## Por que este módulo existe
 *
 * `daily_ops` é append-only por delta e não tem segunda fonte. Quando uma
 * ferramenta precisa saber se ele está certo, ela recomputa a verdade a partir
 * de `labels` — é o que fazem `monitorDailyOpsHealth` (que ACUSA),
 * `scripts/repair-daily-ops.ts` (que ESCREVE) e os dois diagnósticos.
 *
 * Esse recomputo só vale enquanto as etiquetas do dia continuarem existindo. E
 * há um apagador que não é a TTL: o `testTenantUsageSimulator` faz higiene da
 * lista de "Vencidas" das contas de demonstração e apaga FISICAMENTE as `EXPIRED`
 * [EXPIRED_PURGE_AFTER_MS] depois do vencimento (#108). Sem isso o prospect
 * abriria o app numa tela com centenas de vencidas — que nenhum restaurante
 * real teria. A purga fica; quem tem de aprender são as ferramentas.
 *
 * O efeito é assimétrico e é o ponto todo: o agregado contou a expiração no dia
 * em que ela aconteceu (corretamente), e dois dias depois o doc some. A verdade
 * recomputada nesses tenants é portanto **menor** que a real.
 *
 * ## A regra
 *
 * > Em tenant com purga, `expired` recomputado a partir de `labels` é um LOWER
 * > BOUND. O excesso do agregado é explicável e legítimo; a falta não é.
 *
 * É o MESMO tratamento que `created`/`printed` já recebem em todo tenant desde
 * a #230 (lá o apagador é o delete físico anterior à #148). Consequências:
 *
 * - `truth.expired := max(recomputado, agregado)` — ver [applyPurgeFloor].
 * - `dropped` DERIVA de `computeDropped` e acompanha sozinho: nenhuma
 *   ferramenta trata `dropped` à parte.
 * - `consumed`/`discarded`/`mixed` seguem em comparação EXATA — a purga só toca
 *   `EXPIRED`.
 *
 * O preço, aceito conscientemente: um excesso REAL de `expired` nesses dois
 * tenants deixa de ser detectável. Por isso `monitorDailyOpsHealth` DECLARA o
 * relaxamento no relatório do dia (`relaxedTenants`) em vez de silenciá-lo —
 * a lição da #339 é que "não consegui olhar" nunca pode passar por "está tudo
 * bem".
 *
 * Sem `zod` de propósito, como `dailyOpsTerminals` e `labelMutability`: os
 * scripts do repo importam este módulo com `tsx`, direto do source.
 */

import type { DailyOpsTerminalField } from './dailyOpsTerminals';
import { DEMO_TENANT_IDS, TEST_TENANT_IDS } from './seedTenants';

/**
 * Tenants alimentados pelo `testTenantUsageSimulator` — os ÚNICOS onde etiqueta
 * é apagada fora da TTL: os tenants de teste internos (`IT-98765432103`, a
 * bancada; `BR-01010001000180`) e os demos de avaliação por idioma
 * (`demo-account-*`, os que os prospects abrem no celular).
 *
 * É a MESMA constante que o simulador exporta como `SIM_TENANT_IDS` (ele
 * importa daqui), e não uma cópia: a #351 nasceu de uma tabela redigitada em
 * nove lugares, e esta lista tinha três cópias literais quando a #354 foi
 * aberta. Desde a #1003 ela DERIVA do registro `seedTenants.ts` — tenant
 * semeado novo entra aqui sem redigitar.
 *
 * NÃO confundir com `TEST_MODE_BILLING_TENANT_IDS`
 * (`apps/functions/src/lib/billing/testModeTenants.ts`): aquela governa
 * dinheiro de teste e exclui `BR-01010001000180` de propósito, porque um
 * pagamento de teste num tenant BR emitiria NF-e real.
 *
 * O `as const` sobre o spread das duas tuplas é obrigatório: o callable do
 * simulador faz `z.enum(SIM_TENANT_IDS)`, que exige a tupla de literais.
 *
 * Histórico:
 *  - 2026-10-19 12:15 +0200 — (#1003) deriva de `TEST_TENANT_IDS` +
 *    `DEMO_TENANT_IDS`; entram os três `demo-account-*`.
 */
export const LABEL_PURGING_TENANT_IDS = [...TEST_TENANT_IDS, ...DEMO_TENANT_IDS] as const;

const PURGING: ReadonlySet<string> = new Set(LABEL_PURGING_TENANT_IDS);

/**
 * Idade do VENCIMENTO a partir da qual a `EXPIRED` é apagada. Mora aqui, e não
 * no simulador, porque é a janela que EXPLICA o piso: quem lê uma divergência
 * de `expired` precisa saber em quanto tempo o doc some.
 */
export const EXPIRED_PURGE_AFTER_MS = 48 * 3_600_000;

/** `true` se etiqueta desse tenant é apagada fora da TTL. */
export function isLabelPurgingTenant(tenantId: string): boolean {
  return PURGING.has(tenantId);
}

/**
 * Os granulares que a purga esvazia — só `expired`. `dropped` fica de fora de
 * propósito: ele é derivado dos granulares por `computeDropped`, então corrigir
 * `expired` já o corrige.
 */
export const PURGE_LOWER_BOUND_FIELDS: readonly DailyOpsTerminalField[] = ['expired'];

/** Rótulo do relaxamento no relatório do `monitorDailyOpsHealth`. */
export const PURGE_RELAX_REASON = 'SIMULATOR_EXPIRED_PURGE';

/** Contagens de um dia, na forma que cada ferramenta já usa. */
type TerminalCounts = Partial<Record<DailyOpsTerminalField, number>>;

/**
 * Levanta a verdade recomputada até o agregado nos campos que a purga pode ter
 * esvaziado. Devolve uma CÓPIA — o chamador costuma guardar o recomputado cru
 * para a mensagem de diagnóstico.
 *
 * Só chamar quando `isLabelPurgingTenant(tenantId)`: aplicar isto num tenant de
 * cliente esconderia drift real, que é o oposto do que estas ferramentas
 * existem para fazer.
 *
 * Depois do piso, o chamador recalcula `dropped` com `computeDropped` como já
 * fazia — não há caminho em que os dois andem separados.
 */
export function applyPurgeFloor<T extends TerminalCounts>(
  truth: T,
  aggregate: Readonly<TerminalCounts>,
): T {
  const out = { ...truth };
  for (const field of PURGE_LOWER_BOUND_FIELDS) {
    const recomputed = out[field] ?? 0;
    const stored = aggregate[field] ?? 0;
    if (stored > recomputed) {
      (out as TerminalCounts)[field] = stored;
    }
  }
  return out;
}

/**
 * Os campos cuja comparação fica relaxada num tenant com purga — `expired` mais
 * o `dropped` que o contém. É o que o relatório do monitor publica, para quem
 * ler o número daqui a um ano saber que ele foi relaxado e por quê.
 */
export const PURGE_RELAXED_REPORT_FIELDS: readonly string[] = [
  ...PURGE_LOWER_BOUND_FIELDS,
  'dropped',
];
